import { useState } from 'react'
import {
  Control,
  FormState,
  Path,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect'

import { FosterBulkForm } from '@/app/organization/[orgId]/(app)/fosters/bulk'
import FormFieldCheckbox from '@/components/form/FormFieldCheckbox'
import FormFieldDate from '@/components/form/FormFieldDate'
import FosterFecalFollowupModal from '@/components/fosters/cards/FosterFecalFollowupModal'
import fosterSharedStyles from '@/components/fosters/cards/fosterSharedStyles'
import { useFosterFormContext } from '@/components/fosters/FosterFormContext'
import { FosterForm } from '@/components/fosters/fosterFormSchema'
import Card from '@/components/shared/Card'
import InfoText from '@/components/shared/InfoText'
import TourSpot from '@/components/tour/TourSpot'
import Gender from '@/enums/gender'
import MedicalInfoType from '@/enums/medicalInfoType'
import Species from '@/enums/species'
import useUserRoles from '@/hooks/useUserRoles'
import OrganizationService from '@/services/organizationService'

// Props for the bulk form version
interface FosterBulkMedicalFormProps {
  control: Control<FosterBulkForm>
  formState: FormState<FosterBulkForm>
  setValue: UseFormSetValue<FosterBulkForm>
  isLoading: boolean
  disabled: boolean
  showHeartwormTestFields: boolean
  species: Species | undefined
  submit?: () => void
  children?: React.ReactNode
  scrollRef?: React.RefObject<any>
}

const FosterBulkMedicalForm = ({
  control,
  formState,
  setValue,
  isLoading,
  disabled,
  showHeartwormTestFields,
  species,
  submit,
  children,
  scrollRef,
}: FosterBulkMedicalFormProps) => {
  const [fecalFollowUpModalOpen, setFecalFollowUpModalOpen] = useState(false)
  const { isUserAdmin } = useUserRoles()
  // Use useWatch from react-hook-form
  const gender = useWatch({ control, name: 'gender' })
  const birthDate = useWatch({ control, name: 'birthDate' })
  const positiveFecalTestDate = useWatch({
    control,
    name: 'positiveFecalTestDate',
  })
  const spayNeuter =
    gender === Gender.Male
      ? 'Neuter'
      : gender === Gender.Female
        ? 'Spay'
        : undefined
  const fecalMedicalInfos = OrganizationService.useMedicalInfo(
    MedicalInfoType.Fecal,
    birthDate,
    species as Species | undefined
  )
  const spayNeuterMedicalInfos = OrganizationService.useMedicalInfo(
    MedicalInfoType.SpayAndNeuter,
    birthDate,
    species as Species | undefined
  )
  const onFecalFollowUpModalClose = (fecalFollowUpDate?: Date) => {
    if (fecalFollowUpDate) {
      setValue(
        'fecalFollowUpDate',
        fecalFollowUpDate as any // type is correct for FosterBulkForm
      )
    }
    setFecalFollowUpModalOpen(false)
  }
  useDeepCompareEffectNoCheck(() => {
    if (
      positiveFecalTestDate &&
      positiveFecalTestDate instanceof Date &&
      (formState.dirtyFields as FosterBulkForm).positiveFecalTestDate
    ) {
      setFecalFollowUpModalOpen(true)
    }
  }, [positiveFecalTestDate])

  return (
    <Card title="Medical Info">
      <FosterFecalFollowupModal
        isOpen={fecalFollowUpModalOpen}
        onClose={onFecalFollowUpModalClose}
      />
      <View style={fosterSharedStyles.textInputContainer}>
        {!species && (
          <>
            {children && children}
            {isUserAdmin && fecalMedicalInfos?.[0]?.text ? (
              <TourSpot
                id="foster-fecal-medical-info"
                scrollRef={scrollRef}
                style={fosterMedicalFormStylesheet.fullWidth}
              >
                <InfoText text={fecalMedicalInfos?.[0]?.text} />
              </TourSpot>
            ) : (
              <InfoText text={fecalMedicalInfos?.[0]?.text} />
            )}
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'positiveFecalTestDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="💩 Positive Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fecalFollowUpDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="💩 Fecal Follow-up Test Due"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'clearFecalTestDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="✔️ Clear Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <InfoText text={spayNeuterMedicalInfos?.[0]?.text} />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fixedOnDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label={`✂️ ${spayNeuter || 'Spay/Neuter'} Date`}
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            />
            <View
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            >
              <FormFieldCheckbox
                control={control}
                disabled={disabled}
                fieldName={'fixedBeforeIntake' as Path<FosterBulkForm>}
                isLoading={isLoading}
                label={
                  spayNeuter
                    ? `${spayNeuter}ed before intake`
                    : 'Spayed/Neutered before intake'
                }
                onBlur={submit}
                required={false}
                status="checked"
              />
            </View>
          </>
        )}
        {species === Species.Dog && (
          <>
            {children && children}
            {isUserAdmin && fecalMedicalInfos?.[0]?.text ? (
              <TourSpot
                id="foster-fecal-medical-info"
                scrollRef={scrollRef}
                style={fosterMedicalFormStylesheet.fullWidth}
              >
                <InfoText text={fecalMedicalInfos?.[0]?.text} />
              </TourSpot>
            ) : (
              <InfoText text={fecalMedicalInfos?.[0]?.text} />
            )}
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'positiveFecalTestDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="💩 Positive Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fecalFollowUpDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="💩 Fecal Follow-up Test Due"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'clearFecalTestDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="✔️ Clear Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            {showHeartwormTestFields && (
              <View style={fosterMedicalFormStylesheet.medicalInfoInput}>
                <FormFieldDate
                  control={control}
                  disabled={disabled}
                  errors={formState.errors}
                  fieldName={
                    'heartwormTestingCompletedDate' as Path<FosterBulkForm>
                  }
                  isLoading={isLoading}
                  label="✔️ Heartworm Test Completed"
                  onBlur={submit}
                  required={false}
                  style={fosterMedicalFormStylesheet.medicalInfoInput}
                />
                <FormFieldCheckbox
                  control={control}
                  disabled={disabled}
                  fieldName={'heartwormPositive' as Path<FosterBulkForm>}
                  isLoading={isLoading}
                  label="Heartworm Positive"
                  onBlur={submit}
                  required={false}
                  status="checked"
                />
              </View>
            )}
            <InfoText text={spayNeuterMedicalInfos?.[0]?.text} />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fixedOnDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label={`✂️ ${spayNeuter || 'Spay/Neuter'} Date`}
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            />
            <View
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            >
              <FormFieldCheckbox
                control={control}
                disabled={disabled}
                fieldName={'fixedBeforeIntake' as Path<FosterBulkForm>}
                isLoading={isLoading}
                label={
                  spayNeuter
                    ? `${spayNeuter}ed before intake`
                    : 'Spayed/Neutered before intake'
                }
                onBlur={submit}
                required={false}
                status="checked"
              />
            </View>
          </>
        )}
        {species === Species.Cat && (
          <>
            {children && children}
            {isUserAdmin && fecalMedicalInfos?.[0]?.text ? (
              <TourSpot
                id="foster-fecal-medical-info"
                scrollRef={scrollRef}
                style={fosterMedicalFormStylesheet.fullWidth}
              >
                <InfoText text={fecalMedicalInfos?.[0]?.text} />
              </TourSpot>
            ) : (
              <InfoText text={fecalMedicalInfos?.[0]?.text} />
            )}
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'positiveFecalTestDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="💩 Positive Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fecalFollowUpDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="💩 Fecal Follow-up Test Due"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'clearFecalTestDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label="✔️ Clear Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <View style={fosterMedicalFormStylesheet.medicalInfoInput}>
              <FormFieldDate
                control={control}
                disabled={disabled}
                errors={formState.errors}
                fieldName={'comboTestingCompletedDate' as Path<FosterBulkForm>}
                isLoading={isLoading}
                label="✔️ Combo Test Completed"
                onBlur={submit}
                required={false}
                style={fosterMedicalFormStylesheet.medicalInfoInput}
              />
              <View
                style={
                  fosterMedicalFormStylesheet.medicalInfoCatComboCheckboxContainer
                }
              >
                <FormFieldCheckbox
                  control={control}
                  disabled={disabled}
                  fieldName={'fivPositive' as Path<FosterBulkForm>}
                  isLoading={isLoading}
                  label="FIV Positive"
                  onBlur={submit}
                  required={false}
                  status="checked"
                />
                <FormFieldCheckbox
                  control={control}
                  disabled={disabled}
                  fieldName={'felvPositive' as Path<FosterBulkForm>}
                  isLoading={isLoading}
                  label="FeLV Positive"
                  onBlur={submit}
                  required={false}
                  status="checked"
                />
              </View>
            </View>

            <InfoText text={spayNeuterMedicalInfos?.[0]?.text} />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fixedOnDate' as Path<FosterBulkForm>}
              isLoading={isLoading}
              label={`✂️ ${spayNeuter || 'Spay/Neuter'} Date`}
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            />
            <View
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            >
              <FormFieldCheckbox
                control={control}
                disabled={disabled}
                fieldName={'fixedBeforeIntake' as Path<FosterBulkForm>}
                isLoading={isLoading}
                label={
                  spayNeuter
                    ? `${spayNeuter}ed before intake`
                    : 'Spayed/Neutered before intake'
                }
                onBlur={submit}
                required={false}
                status="checked"
              />
            </View>
          </>
        )}
      </View>
    </Card>
  )
}

// Props for the single foster form version
interface FosterSingleMedicalFormProps {
  children?: React.ReactNode
  submit?: () => void
  scrollRef?: React.RefObject<any>
}

const FosterSingleMedicalForm = ({
  children,
  submit,
  scrollRef,
}: FosterSingleMedicalFormProps) => {
  const form = useFosterFormContext()

  const { control, formState, setValue } = form

  const [fecalFollowUpModalOpen, setFecalFollowUpModalOpen] = useState(false)
  const { isUserAdmin } = useUserRoles()
  const watch = form.watch
  const gender = watch('gender')
  const birthDate = watch('birthDate')
  const positiveFecalTestDate = watch('positiveFecalTestDate')
  const spayNeuter =
    gender === Gender.Male
      ? 'Neuter'
      : gender === Gender.Female
        ? 'Spay'
        : undefined
  const species = watch('species') ?? undefined
  const showHeartwormTestFields = false // or derive from context if needed
  const isLoading = false // or derive from context if needed
  const disabled = false // or derive from context if needed
  const fecalMedicalInfos = OrganizationService.useMedicalInfo(
    MedicalInfoType.Fecal,
    birthDate,
    species as Species | undefined
  )
  const spayNeuterMedicalInfos = OrganizationService.useMedicalInfo(
    MedicalInfoType.SpayAndNeuter,
    birthDate,
    species as Species | undefined
  )
  const onFecalFollowUpModalClose = (fecalFollowUpDate?: Date) => {
    if (fecalFollowUpDate) {
      setValue(
        'fecalFollowUpDate',
        fecalFollowUpModalOpen as any // type is correct for FosterForm
      )
    }
    setFecalFollowUpModalOpen(false)
  }
  useDeepCompareEffectNoCheck(() => {
    if (
      positiveFecalTestDate &&
      positiveFecalTestDate instanceof Date &&
      formState.dirtyFields.positiveFecalTestDate
    ) {
      setFecalFollowUpModalOpen(true)
    }
  }, [positiveFecalTestDate])

  return (
    <Card title="Medical Info">
      <FosterFecalFollowupModal
        isOpen={fecalFollowUpModalOpen}
        onClose={onFecalFollowUpModalClose}
      />
      <View style={fosterSharedStyles.textInputContainer}>
        {!species && (
          <>
            {children && children}
            {isUserAdmin && fecalMedicalInfos?.[0]?.text ? (
              <TourSpot
                id="foster-fecal-medical-info"
                scrollRef={scrollRef}
                style={fosterMedicalFormStylesheet.fullWidth}
              >
                <InfoText text={fecalMedicalInfos?.[0]?.text} />
              </TourSpot>
            ) : (
              <InfoText text={fecalMedicalInfos?.[0]?.text} />
            )}
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'positiveFecalTestDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="💩 Positive Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fecalFollowUpDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="💩 Fecal Follow-up Test Due"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'clearFecalTestDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="✔️ Clear Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <InfoText text={spayNeuterMedicalInfos?.[0]?.text} />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fixedOnDate' as Path<FosterForm>}
              isLoading={isLoading}
              label={`✂️ ${spayNeuter || 'Spay/Neuter'} Date`}
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            />
            <View
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            >
              <FormFieldCheckbox
                control={control}
                disabled={disabled}
                fieldName={'fixedBeforeIntake' as Path<FosterForm>}
                isLoading={isLoading}
                label={
                  spayNeuter
                    ? `${spayNeuter}ed before intake`
                    : 'Spayed/Neutered before intake'
                }
                onBlur={submit}
                required={false}
                status="checked"
              />
            </View>
          </>
        )}
        {species === Species.Dog && (
          <>
            {children && children}
            {isUserAdmin && fecalMedicalInfos?.[0]?.text ? (
              <TourSpot
                id="foster-fecal-medical-info"
                scrollRef={scrollRef}
                style={fosterMedicalFormStylesheet.fullWidth}
              >
                <InfoText text={fecalMedicalInfos?.[0]?.text} />
              </TourSpot>
            ) : (
              <InfoText text={fecalMedicalInfos?.[0]?.text} />
            )}
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'positiveFecalTestDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="💩 Positive Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fecalFollowUpDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="💩 Fecal Follow-up Test Due"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'clearFecalTestDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="✔️ Clear Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            {showHeartwormTestFields && (
              <View style={fosterMedicalFormStylesheet.medicalInfoInput}>
                <FormFieldDate
                  control={control}
                  disabled={disabled}
                  errors={formState.errors}
                  fieldName={
                    'heartwormTestingCompletedDate' as Path<FosterForm>
                  }
                  isLoading={isLoading}
                  label="✔️ Heartworm Test Completed"
                  onBlur={submit}
                  required={false}
                  style={fosterMedicalFormStylesheet.medicalInfoInput}
                />
                <FormFieldCheckbox
                  control={control}
                  disabled={disabled}
                  fieldName={'heartwormPositive' as Path<FosterForm>}
                  isLoading={isLoading}
                  label="Heartworm Positive"
                  onBlur={submit}
                  required={false}
                  status="checked"
                />
              </View>
            )}

            <InfoText text={spayNeuterMedicalInfos?.[0]?.text} />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fixedOnDate' as Path<FosterForm>}
              isLoading={isLoading}
              label={`✂️ ${spayNeuter || 'Spay/Neuter'} Date`}
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            />
            <View
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            >
              <FormFieldCheckbox
                control={control}
                disabled={disabled}
                fieldName={'fixedBeforeIntake' as Path<FosterForm>}
                isLoading={isLoading}
                label={
                  spayNeuter
                    ? `${spayNeuter}ed before intake`
                    : 'Spayed/Neutered before intake'
                }
                onBlur={submit}
                required={false}
                status="checked"
              />
            </View>
          </>
        )}
        {species === Species.Cat && (
          <>
            {children && children}
            {isUserAdmin && fecalMedicalInfos?.[0]?.text ? (
              <TourSpot
                id="foster-fecal-medical-info"
                scrollRef={scrollRef}
                style={fosterMedicalFormStylesheet.fullWidth}
              >
                <InfoText text={fecalMedicalInfos?.[0]?.text} />
              </TourSpot>
            ) : (
              <InfoText text={fecalMedicalInfos?.[0]?.text} />
            )}
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'positiveFecalTestDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="💩 Positive Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fecalFollowUpDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="💩 Fecal Follow-up Test Due"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'clearFecalTestDate' as Path<FosterForm>}
              isLoading={isLoading}
              label="✔️ Clear Fecal Test Date"
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
            <View style={fosterMedicalFormStylesheet.medicalInfoInput}>
              <FormFieldDate
                control={control}
                disabled={disabled}
                errors={formState.errors}
                fieldName={'comboTestingCompletedDate' as Path<FosterForm>}
                isLoading={isLoading}
                label="✔️ Combo Test Completed"
                onBlur={submit}
                required={false}
                style={fosterMedicalFormStylesheet.medicalInfoInput}
              />
              <View
                style={
                  fosterMedicalFormStylesheet.medicalInfoCatComboCheckboxContainer
                }
              >
                <FormFieldCheckbox
                  control={control}
                  disabled={disabled}
                  fieldName={'fivPositive' as Path<FosterForm>}
                  isLoading={isLoading}
                  label="FIV Positive"
                  onBlur={submit}
                  required={false}
                  status="checked"
                />
                <FormFieldCheckbox
                  control={control}
                  disabled={disabled}
                  fieldName={'felvPositive' as Path<FosterForm>}
                  isLoading={isLoading}
                  label="FeLV Positive"
                  onBlur={submit}
                  required={false}
                  status="checked"
                />
              </View>
            </View>

            <InfoText text={spayNeuterMedicalInfos?.[0]?.text} />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'fixedOnDate' as Path<FosterForm>}
              isLoading={isLoading}
              label={`✂️ ${spayNeuter || 'Spay/Neuter'} Date`}
              onBlur={submit}
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            />
            <View
              style={fosterMedicalFormStylesheet.medicalInfoInputConstrained}
            >
              <FormFieldCheckbox
                control={control}
                disabled={disabled}
                fieldName={'fixedBeforeIntake' as Path<FosterForm>}
                isLoading={isLoading}
                label={
                  spayNeuter
                    ? `${spayNeuter}ed before intake`
                    : 'Spayed/Neutered before intake'
                }
                onBlur={submit}
                required={false}
                status="checked"
              />
            </View>
          </>
        )}
      </View>
    </Card>
  )
}

// Main FosterMedicalForm wrapper
const FosterMedicalForm = (props: any) => {
  if (props.control && props.formState && props.setValue) {
    // Bulk version
    return <FosterBulkMedicalForm {...props} />
  } else {
    // Single foster version
    return <FosterSingleMedicalForm {...props} />
  }
}

export default FosterMedicalForm

export const fosterMedicalFormStylesheet = StyleSheet.create(() => {
  return {
    fullWidth: {
      width: '100%',
    },
    medicalInfoCatComboCheckboxContainer: {
      flexDirection: 'row',
    },
    medicalInfoInput: {
      flexBasis: {
        xs: '50%',
        sm: '40%',
        lg: '20%',
      },
      flexGrow: 1,
      minWidth: 150,
    },
    medicalInfoInputConstrained: {
      flexBasis: {
        xs: '50%',
        sm: '40%',
        lg: '20%',
      },
      flexGrow: 1,
      minWidth: 150,
    },
  }
})
